import { useEffect, useState } from "react";
import { logEvent } from "../../utils/analytics";
import { BsArrowLeft } from 'react-icons/bs';
import { MdClose } from "react-icons/md";
import API from '../../utils/api';
import Input from './Input.jsx';
import Button from './Button.jsx';

export const USER_REGISTRATION_KEY = "user-registered";
const RegisterForm = ({ onClose, onRegister }) => {

    const [step, setStep] = useState("phone")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [formattedNumber, setFormattedNumber] = useState("")
    const [otpParameters, setOtpParamenters] = useState({ pinId: null, otpLength: null })
    const [otp, setOtp] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState("");

    const [resendCountdown, setResendCountdown] = useState(0)
    const canResend = step === "otp" && resendCountdown === 0;
    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
    }

    useEffect(() => {
        let timer;
        if (resendCountdown > 0) {
            timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1)
            }, 1000)
        }

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [resendCountdown, step])

    const handlePhoneSubmit = async (e) => {
        e.preventDefault()
        if (!phoneNumber.trim() || phoneNumber.length < 10) {
            setError("Please enter a valid phone number")
            return
        }

        sendOtp();
    }

    const sendOtp = async () => {

        logEvent('Registration', 'Send OTP', 'Get OTP Code');
        setIsLoading(true);
        setError("");
        setOtp("");


        const formattedNumber = phoneNumber.startsWith("0") ? phoneNumber.substring(1) : phoneNumber;
        const fullNumber = `+234${formattedNumber}`;
        setFormattedNumber(fullNumber);

        await API.registerPhoneNumber(fullNumber)
            .then((res) => {
                setOtpParamenters(res.data.data);
                setResendCountdown(res.data.data.retryTimeout)
                setStep("otp")
            })
            .catch(() => {
                setError("We couldn't complete that request, pls try again")
            }).finally(() => {
                setIsLoading(false)
            });
    }

    const handleOtpSubmit = async (e) => {
        e.preventDefault()

        if (otp.length !== otpParameters.otpLength) {
            setError(`Please enter a valid ${otpParameters.otpLength}-digit code`)
            return
        }

        logEvent('Registration', 'Verify OTP', 'Verify Code');
        setIsLoading(true);
        setError("");

        await API.verifyOTP({ phone: formattedNumber, code: otp, pinId: otpParameters.pinId })
            .then(() => {
                localStorage.setItem(USER_REGISTRATION_KEY, "true")
                completeStep();
            })
            .catch((err) => {
                let errorMessage = "We couldn't verify your OTP, pls try again";
                if (err.status === 409) errorMessage = "Pls make sure you entered the correct OTP";
                setError(errorMessage)
            }).finally(() => {
                setIsLoading(false)
            });
    }

    const handleResendCode = async () => {
        logEvent('Registration', 'Resend OTP code', 'Resend');

        if (!canResend || isLoading) return

        sendOtp();
    }

    const onClickBack = () => {
        logEvent('Registration', 'Clicked back to phone number step', 'Back button');

        setError("");
        setStep("phone")
    }
    const onCloseDrawer = () => {
        onClose();
        logEvent('Registration', 'Closed Drawer', 'Close button');
    }

    const completeStep = () => {
        onRegister();
        setStep("phone")
        setPhoneNumber("")
        setOtp("")
        setError("")
    }

    return <div className="flex flex-col items-center overflow-hidden border-0">

        {<button onClick={onCloseDrawer} className="m-4 text-sm text-neutral-500 ml-auto underline">
            <MdClose className="h-5 w-5" />
        </button>}

        <div className="w-full flex flex-col items-center gap-2 py-8">

            {step === "phone" &&

                <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-1 md:max-w-[600px]">
                    <header className="pb-8">
                        <h2 className="text-xl font-bold">Verify your phone number</h2>
                        <p className="text text-neutral-500">To stay informed on latest election updates</p>
                    </header>

                    <label className="text-neutral-900 center font-semibold">
                        Phone Number
                    </label>
                    <Input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={isLoading}>
                        <span className="border-r h-full pr-2  tracking-wide">+234</span>
                    </Input>
                    {error && <p className="text-xs pl-1 text-red-600">{error}</p>}

                    <div className="flex flex-col py-2 gap-3">
                        <Button type="submit" disabled={isLoading} flush>
                            Get OTP Code
                        </Button>
                        <p className="text-xs text-neutral-500">We'll send you a code to verify your number</p>
                    </div>
                </form>
            }
            {step === "otp" && <>

                <form onSubmit={handleOtpSubmit} className="flex flex-col gap-1">
                    <div className="w-full pb-8">
                        <button onClick={onClickBack} className="inline-flex gap-1 items-center text-neutral-700 hover:text-neutral-800">
                            <BsArrowLeft className="h-4 w-4" />
                            <span className="text-sm">Back</span>
                        </button>
                    </div>

                    <label className="text-neutral-900 font-semibold ">
                        Enter OTP Code
                    </label>
                    <p className="text-xs text-neutral-500 pb-2">Check the {otpParameters.otpLength}-digit code we sent to
                        <span className="font-bold text-neutral-700 tracking-wide"> {formattedNumber}</span>
                        <br />
                    </p>

                    <Input
                        type="text"
                        placeholder="123456"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="tracking-widest"
                        disabled={isLoading}
                        maxLength={6}
                    />

                    {error && <p className="text-xs text-red-600">{error}</p>}


                    <div className="flex flex-col py-2 gap-3">
                        <Button type="submit" disabled={isLoading} flush>
                            Verify Code
                        </Button>
                        <p className="text-xs text-neutral-500">
                            Didn't get a code?
                            {canResend ?
                                <button className="underline pl-1" onClick={handleResendCode}>Resend</button>
                                : <span className="pl-1">You can resend in {formatCountdown(resendCountdown)}</span>
                            }
                        </p>

                    </div>
                </form>
            </>}

        </div>

    </div >
}
export default RegisterForm;
